import { type RootState } from '@/store';
import { POLICY_EVALUATION } from '@/tauri/force-update/forceUpdatePolicy';
import { isTauri } from '@/tauri/utils';
import { appendAmplitudeCrossDomainParams } from '@/utils/amplitudeCrossDomain';
import { appendAttributionParamsToRedirectUrl } from '@/utils/attributionParams';
import { prefixWithLocale } from '@/utils/localeRedirect';
import { useTranslations } from '@/utils/translations';
import { isValidMobileSignupSource, mobileSignupSourceAtom } from '@heidi/growth/data';
import { zodResolver } from '@hookform/resolvers/zod';
import { useSetAtom } from 'jotai';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { KINDE_EMAIL_CONNECTION_ID } from '../../../constants/kinde';
import {
  emailFormSchema,
  isValidConnectionId,
  isValidSsoEmail,
  type EmailFormData,
} from '../utils';

interface UseSignInModalStateProps {
  postLoginRedirectUri?: string;
}

/**
 * Custom hook that manages common state and logic for sign-in modals
 */
export function useSignInModalState({
  postLoginRedirectUri: initialPostLoginRedirectUri,
}: UseSignInModalStateProps) {
  const router = useRouter();
  const { locale } = useTranslations();
  const setMobileSignupSource = useSetAtom(mobileSignupSourceAtom);

  // Helper function to add locale prefix if not already present
  const getRedirectUrl = (path: string) => prefixWithLocale(locale, path);

  // Use connection_id from URL query param if valid, otherwise fallback to default
  const connectionIdFromQuery = router.query.connection_id;
  const emailConnectionId =
    typeof connectionIdFromQuery === 'string' &&
    isValidConnectionId(connectionIdFromQuery)
      ? connectionIdFromQuery
      : KINDE_EMAIL_CONNECTION_ID;

  // Form state
  const {
    control,
    watch,
    reset,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<EmailFormData>({
    resolver: zodResolver(emailFormSchema),
    defaultValues: { email: '' },
    mode: 'onBlur',
  });

  const email = watch('email');
  const isEmailInvalid = !!errors.email;

  // Hide sign-in modal when force update is active (forced update)
  const forceUpdateActive = useSelector((state: RootState) => {
    if (!isTauri()) return false;
    const result = state.forceUpdateModel?.evaluationResult;
    return result === POLICY_EVALUATION.FORCED;
  });

  // default this to the props redirect uri unless it's set in the query params
  const [postLoginRedirectUri, setPostLoginRedirectUri] = useState(
    initialPostLoginRedirectUri
  );

  // we set this to true by default to avoid flashing the modal on and hide it right after
  // since kinde takes some time to load, it's best not to show the modal to avoid users start typing
  // their login info while kinde is loading
  const [isUsingSsoOrg, setIsUsingSsoOrg] = useState(true);

  // Mobile webview signup: persist source param and pre-fill email from loginHint
  // Compute initial value synchronously from URL to avoid flash of modal before loader
  const isFromMobileWebview = useMemo(() => {
    if (typeof window === 'undefined') return false;
    const params = new URLSearchParams(window.location.search);
    const source = params.get('source');
    const loginHint = params.get('loginHint');
    return (source === 'ios' || source === 'android') && !!loginHint;
  }, []);

  // Update postLoginRedirectUri from query params
  useEffect(() => {
    const postLoginRedirectUrlFromQuery = router.query.post_login_redirect_url as string;

    if (postLoginRedirectUrlFromQuery) {
      setPostLoginRedirectUri(
        appendAttributionParamsToRedirectUrl(postLoginRedirectUrlFromQuery)
      );
    }
  }, [router]);

  // Handle SSO org redirect
  useEffect(() => {
    if (!router.query.sso_org || typeof router.query.sso_org !== 'string') {
      setIsUsingSsoOrg(false);
      return;
    }

    // using a generic sso email will make kinde redirect to the sso org login page
    // that matches the email domain (org) provided
    const ssoEmail = `sso@${router.query.sso_org}`;

    // make sure the sso_org domain passed generates a valid email
    if (!isValidSsoEmail(ssoEmail)) {
      setIsUsingSsoOrg(false);
      return;
    }

    const ssoPostLoginRedirectUri = appendAmplitudeCrossDomainParams(
      appendAttributionParamsToRedirectUrl(
        getRedirectUrl(postLoginRedirectUri ?? '/scribe/new-user')
      )
    );
    // redirect to kinde and let it handle login
    void router.push({
      pathname: `/api/auth/login`,
      query: {
        login_hint: ssoEmail,
        post_login_redirect_url: ssoPostLoginRedirectUri,
        connection_id: emailConnectionId,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- getRedirectUrl depends on locale which is stable
  }, [router, postLoginRedirectUri, emailConnectionId, locale]);

  useEffect(() => {
    if (!router.isReady) return;

    const source = router.query.source;
    if (typeof source === 'string' && isValidMobileSignupSource(source)) {
      setMobileSignupSource(source);
    }

    const loginHint = router.query.loginHint;
    if (typeof loginHint === 'string' && loginHint) {
      reset({ email: loginHint });
    }
  }, [
    router.isReady,
    router.query.source,
    router.query.loginHint,
    setMobileSignupSource,
    reset,
  ]);

  return {
    control,
    email,
    isEmailInvalid,
    setError,
    clearErrors,
    emailConnectionId,
    forceUpdateActive,
    postLoginRedirectUri,
    isUsingSsoOrg,
    isFromMobileWebview,
  };
}
